import { useEffect, useRef, useState } from "react";
import { Check, ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { issues, kindLabel } from "@/lib/questionRules";
import { ScenarioForm } from "@/pages/assessments/builder/ScenarioForm";
import { QuestionForm } from "@/pages/assessments/builder/QuestionForm";
import { CandidatePreview } from "@/pages/assessments/builder/CandidatePreview";

export function QuestionDrawer({ section, index, onIndex, onChangeItem, onAddAnother, onClose, canEdit, autosave }) {
  const items = section.type === "scenarios" ? section.scenarios : section.questions;
  const item = index != null ? items[index] : null;
  const [saved, setSaved] = useState("idle");
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  if (!item) return <Sheet open={false} />;
  const errs = issues(item, section.type);
  const change = (x) => {
    onChangeItem(index, { ...x, ai_draft: false });
    setSaved("saving");
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => setSaved((await autosave?.()) === false ? "local" : "saved"), 900);
  };
  const status = { idle: "", saving: "Saving…", saved: "Saved", local: "Kept in this draft. Click Save draft to keep it." }[saved];
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-[560px]" data-testid="question-drawer" data-tour="question-drawer">
        <SheetHeader className="sticky top-0 z-10 space-y-1 border-b border-line bg-white px-6 py-4 text-left">
          <SheetTitle className="text-lg">{section.type === "scenarios" ? "Scenario" : "Question"} {index + 1} of {items.length}</SheetTitle>
          <SheetDescription className="flex items-center gap-2">
            <span>{kindLabel(item, section.type)} · {section.title}</span>
            {status && <span className="ml-auto inline-flex items-center gap-1 text-xs text-ink-muted" role="status" data-testid="drawer-saved">{saved === "saved" && <Check size={13} className="text-success" />}{status}</span>}
          </SheetDescription>
        </SheetHeader>
        <div className="flex-1 space-y-6 px-6 py-5">
          {section.type === "scenarios"
            ? <ScenarioForm item={item} onChange={change} errs={errs} canEdit={canEdit} />
            : <QuestionForm q={item} secType={section.type} onChange={change} errs={errs} canEdit={canEdit} />}
          <CandidatePreview key={item.id + item.kind} item={item} secType={section.type} />
        </div>
        <div className="sticky bottom-0 flex flex-wrap items-center gap-2 border-t border-line bg-white px-6 py-3">
          <Button variant="outline" size="sm" disabled={index === 0} onClick={() => onIndex(index - 1)} data-testid="drawer-prev"><ChevronLeft size={15} /> Prev</Button>
          <Button variant="outline" size="sm" disabled={index === items.length - 1} onClick={() => onIndex(index + 1)} data-testid="drawer-next">Next <ChevronRight size={15} /></Button>
          <span className="ml-auto flex gap-2">
            {canEdit && <Button variant="outline" size="sm" onClick={onAddAnother} data-testid="drawer-add-another"><Plus size={14} /> Save & add another</Button>}
            <Button size="sm" onClick={onClose} data-testid="drawer-done">Done</Button>
          </span>
        </div>
      </SheetContent>
    </Sheet>
  );
}
